<template>
  <div
    class="ai-skill-overlay"
    @click.self="$emit('close')"
    @wheel.self.prevent
    @mousedown.stop
    @mousemove.stop
    @mouseup.stop
  >
    <aside class="ai-skill-panel" @click.stop @wheel.stop>
      <div class="panel-header">
        <div class="header-left">
          <img src="@/assets/newLogo.svg" alt="Codelos" />
          <span>CodelosBot</span>
          <span class="beta-badge">Beta</span>
        </div>
        <div class="header-right">
          <button type="button" title="Schließen" @click="$emit('close')">
            <i class="fas fa-times"></i>
          </button>
        </div>
      </div>

      <div class="panel-body" ref="bodyRef">
        <div v-if="messages.length === 0" class="welcome-state">
          <div class="welcome-avatar">
            <img src="@/assets/newLogo.svg" alt="AI" />
          </div>
          <h4>Wie kann ich heute helfen?</h4>

          <div class="suggestion-list">
            <button
              v-for="suggestion in suggestions"
              :key="suggestion"
              class="suggestion-item"
              type="button"
              @click="onSuggestionClick(suggestion)"
            >
              {{ suggestion }}
            </button>
          </div>
        </div>

        <div v-else class="messages-list">
          <div
            v-for="(message, idx) in messages"
            :key="message.id || `${message.role}-${idx}`"
            class="message-row"
            :class="message.role"
          >
            <div v-if="message.role === 'workflow'" class="workflow-suggestion">
              <h5>Vorgeschlagene Workflow-Schritte</h5>
              <div class="step-list">
                <div
                  v-for="(task, taskIdx) in getWorkflowSuggestionTasks(
                    message.workflow
                  )"
                  :key="`workflow-step-${message.id || idx}-${taskIdx}`"
                  class="step-item task position-relative"
                >
                  <div class="task-icon-wrapper">
                    <img
                      :src="getSuggestedTaskIcon(task.name)"
                      class="task-icon"
                      alt="task-icon"
                    />
                  </div>
                  <div class="step-caption">{{ task.caption }}</div>
                </div>
              </div>
              <div class="workflow-action-buttons">
                <button
                  type="button"
                  class="secondary"
                  :disabled="Boolean(resolvedIsActionLoading)"
                  @click="onStartenClick(message)"
                >
                  Starten
                </button>
                <div class="action-split">
                  <button
                    type="button"
                    class="secondary with-chevron"
                    :disabled="Boolean(resolvedIsActionLoading)"
                    @click="onSaveClick(message, 'current')"
                  >
                    <span class="btn-label">
                      <i
                        v-if="
                          resolvedIsActionLoading &&
                          resolvedLoadingAction === 'save' &&
                          localLoadingMessageId === message.id
                        "
                        class="fas fa-spinner fa-spin"
                      ></i>
                      <span v-else>Speichern</span>
                    </span>
                    <i
                      v-if="
                        showApplyTargetModeToggle &&
                        !(
                          resolvedIsActionLoading &&
                          resolvedLoadingAction === 'save' &&
                          localLoadingMessageId === message.id
                        )
                      "
                      class="fas fa-chevron-down btn inline-chevron"
                      @click.stop="toggleActionMenu(message, 'save')"
                    ></i>
                  </button>
                  <div
                    v-if="isActionMenuOpen(message, 'save')"
                    class="split-menu"
                  >
                    <button type="button" @click="onSaveClick(message, 'new')">
                      Als neu speichern
                    </button>
                  </div>
                </div>
                <div class="action-split">
                  <button
                    type="button"
                    class="primary with-chevron"
                    :disabled="Boolean(resolvedIsActionLoading)"
                    @click="onEditClick(message, 'current')"
                  >
                    <span class="btn-label">
                      <i
                        v-if="
                          resolvedIsActionLoading &&
                          resolvedLoadingAction === 'edit' &&
                          localLoadingMessageId === message.id
                        "
                        class="fas fa-spinner fa-spin"
                      ></i>
                      <span v-else>Bearbeiten</span>
                    </span>
                    <i
                      v-if="
                        showApplyTargetModeToggle &&
                        !(
                          resolvedIsActionLoading &&
                          resolvedLoadingAction === 'edit' &&
                          localLoadingMessageId === message.id
                        )
                      "
                      class="fas fa-chevron-down btn inline-chevron"
                      @click.stop="toggleActionMenu(message, 'edit')"
                    ></i>
                  </button>
                  <div
                    v-if="isActionMenuOpen(message, 'edit')"
                    class="split-menu"
                  >
                    <button type="button" @click="onEditClick(message, 'new')">
                      Als neu bearbeiten
                    </button>
                  </div>
                </div>
              </div>
            </div>
            <div v-else-if="message.role === 'preview'" class="start-preview">
              <ProcessPreview
                :executionUrl="message.preview.executionUrl"
                :isActionFlow="true"
                :processID="message.preview.flowId"
                :params="message.preview.params"
                :useContainerWidthForResponsive="true"
              />
            </div>
            <div
              v-else
              class="message-bubble"
              v-html="getFormattedMessage(message.text)"
            ></div>
          </div>
          <div v-if="isFetching" class="message-row ai">
            <div class="message-bubble message-loading">
              <span class="dot"></span>
              <span class="dot"></span>
              <span class="dot"></span>
            </div>
          </div>
        </div>
      </div>
      <div class="panel-input">
        <div
          class="input-area"
          :class="{ 'is-dragging-files': isDraggingFiles }"
          @dragenter.prevent="onDragEnterFiles"
          @dragover.prevent="onDragOverFiles"
          @dragleave.prevent="onDragLeaveFiles"
          @drop.prevent="onDropFiles"
        >
          <div v-if="contextChipLabel" class="context-chip">
            <i class="fas fa-layer-group"></i>
            {{ contextChipLabel }}
          </div>
          <div v-if="attachedFiles.length" class="attached-files">
            <div
              v-for="(file, fileIdx) in attachedFiles"
              :key="`${file.name}-${file.size}-${file.lastModified}-${fileIdx}`"
              class="attached-file-chip"
            >
              <span class="name">{{ file.name }}</span>
              <button
                type="button"
                class="remove-chip"
                title="Datei entfernen"
                @click="removeAttachedFile(fileIdx)"
              >
                <i class="fas fa-times"></i>
              </button>
            </div>
          </div>
          <textarea
            ref="promptInputRef"
            v-model="prompt"
            rows="1"
            placeholder="Codelos KI fragen... oder Dateien per Drag & Drop hinzufügen"
            @input="onPromptInput"
            @click="captureCaretPosition"
            @keyup="captureCaretPosition"
            @keydown="onKeydown"
          />
          <ul v-if="showMentionDropdown" class="skill-mention-list">
            <li
              v-for="skill in filteredSkillSuggestions"
              :key="skill"
              @mousedown.prevent="onSelectSkill(skill)"
            >
              @{{ skill }}
            </li>
          </ul>
          <div v-if="promptMentionedSkills.length" class="active-mentions">
            <span
              v-for="skill in promptMentionedSkills"
              :key="`active-${skill}`"
              class="mention-chip"
            >
              @{{ skill }}
            </span>
          </div>
        </div>
        <button
          type="button"
          :disabled="
            ((!prompt || !prompt.trim()) && attachedFiles.length < 1) ||
            isFetching
          "
          @click="sendMessage"
        >
          <i class="fas fa-arrow-up"></i>
        </button>
      </div>
    </aside>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import {
  workFlowTasksMiscData,
  workFlowTasksJsonV3,
} from '@/utils/constants/WorkFlowConstants';
import ProcessPreview from '@/components/Process/ProcessPreview.vue';
import { askChatGPT } from '@/services/workflow.service';
import { getPreSignedUrl, uploadFile } from '@/utils/helpers';
import {
  fetchAISkillSuggestions,
  getCachedAISkillMetadataMap,
  getCachedAISkillSuggestions,
} from '@/utils/helpers/AISkillHelper';

export default {
  name: 'AISkillPanel',
  emits: ['close', 'applyWorkflowSuggestion', 'saveWorkflow', 'startWorkflow'],
  props: {
    flowRunnerEndpoint: {
      type: String,
      required: true,
    },
    activeTaskContext: {
      type: Object,
      default: null,
    },
    currentWorkflow: {
      type: Object,
      default: null,
    },
    workflowName: {
      type: String,
      default: '',
    },
    defaultContextChip: {
      type: String,
      default: '',
    },
    activeDatabaseContext: {
      type: Object,
      default: null,
    },
    showContextChip: {
      type: Boolean,
      default: true,
    },
    isActionLoading: {
      type: Boolean,
      default: false,
    },
    loadingAction: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      prompt: '',
      caretPosition: 0,
      messages: [],
      skillSuggestions: [],
      skillMetadataMap: {},
      isSkillsLoading: false,
      isFetching: false,
      attachedFiles: [],
      isDraggingFiles: false,
      localIsActionLoading: false,
      localLoadingAction: '',
      localLoadingMessageId: null,
      actionMenu: {
        messageId: null,
        action: '',
      },
      suggestions: ['Workflow erstellen'],
    };
  },
  computed: {
    ...mapGetters({
      databaseList: 'getDatabaseList',
      userId: 'getUserId',
    }),
    mentionQuery() {
      const contentBeforeCaret = this.prompt.slice(0, this.caretPosition);
      const mentionMatch = contentBeforeCaret.match(/(^|\s)@([^\s@]*)$/);
      if (!mentionMatch) return null;
      return mentionMatch[2] || '';
    },
    filteredSkillSuggestions() {
      if (this.mentionQuery === null) return [];
      const query = this.mentionQuery.toLowerCase();
      if (!query) return this.skillSuggestions;
      return this.skillSuggestions.filter((skill) =>
        skill.toLowerCase().includes(query)
      );
    },
    showMentionDropdown() {
      return (
        this.mentionQuery !== null && this.filteredSkillSuggestions.length > 0
      );
    },
    promptMentionedSkills() {
      if (!this.prompt || !this.skillSuggestions.length) return [];
      const normalizedSkillSet = new Set(
        this.skillSuggestions.map((skill) => skill.toLowerCase())
      );
      const mentionMatches = this.prompt.match(/@([^\s@]+)/g) || [];
      const mentions = mentionMatches
        .map((mention) => mention.slice(1).trim())
        .filter(
          (mention) => mention && normalizedSkillSet.has(mention.toLowerCase())
        );
      return [...new Set(mentions)];
    },
    activeTaskUsecase() {
      const taskName = String(this.activeTaskContext?.name || '');
      if (taskName === 'Start' || taskName === 'Form') {
        return 'form_builder_skill';
      }
      if (taskName === 'PageBuilder') {
        return 'page_builder_skill';
      }
      return '';
    },
    contextChipLabel() {
      if (!this.showContextChip) return '';
      if (this.activeTaskUsecase) {
        return (
          this.activeTaskContext?.caption ||
          this.activeTaskContext?.name ||
          'Aufgabenkontext'
        );
      }
      if (this.defaultContextChip) return this.defaultContextChip;
      return 'Workflow angebotskalkulation.';
    },
    resolvedIsActionLoading() {
      return Boolean(this.isActionLoading || this.localIsActionLoading);
    },
    resolvedLoadingAction() {
      if (this.isActionLoading && this.loadingAction) {
        return this.loadingAction;
      }
      return this.localLoadingAction;
    },
    showApplyTargetModeToggle() {
      return Boolean(
        this.currentWorkflow && Array.isArray(this.currentWorkflow.tasks)
      );
    },
  },
  methods: {
    mergeMissingDeep(defaultValue, currentValue) {
      if (Array.isArray(currentValue)) return currentValue;
      if (
        currentValue &&
        typeof currentValue === 'object' &&
        !Array.isArray(currentValue)
      ) {
        const base =
          defaultValue &&
          typeof defaultValue === 'object' &&
          !Array.isArray(defaultValue)
            ? { ...defaultValue }
            : {};
        const result = { ...base };
        Object.keys(currentValue).forEach((key) => {
          result[key] = this.mergeMissingDeep(base[key], currentValue[key]);
        });
        return result;
      }
      return currentValue === undefined ? defaultValue : currentValue;
    },
    getDefaultTaskSchema(taskName) {
      return workFlowTasksJsonV3.find((task) => task.name === taskName) || null;
    },
    normalizeWorkflowTasks(tasks = []) {
      const idSeed = Date.now();
      const normalizedTasks = [];

      tasks.forEach((task, index) => {
        const defaultTask = this.getDefaultTaskSchema(task?.name) || {};
        const fallbackId = idSeed + index + 1;
        const taskId =
          task?.id !== undefined && task?.id !== null ? task.id : fallbackId;
        const mergedShape = this.mergeMissingDeep(defaultTask.shape || {}, {
          x: 420 + index * 210,
          y: 320,
          ...(task?.shape || {}),
        });
        const previousTaskIdProvided =
          task?.previousTaskId !== undefined && task?.previousTaskId !== null;

        const normalizedTask = {
          ...defaultTask,
          ...task,
          id: taskId,
          name: task?.name || defaultTask?.name || `Task_${String(index + 1)}`,
          caption:
            task?.caption ||
            defaultTask?.caption ||
            task?.name ||
            `Task ${index + 1}`,
          props: this.mergeMissingDeep(
            defaultTask.props || {},
            task?.props || {}
          ),
          input: Array.isArray(task?.input)
            ? task.input
            : Array.isArray(defaultTask?.input)
            ? defaultTask.input
            : [],
          output: Array.isArray(task?.output)
            ? task.output
            : Array.isArray(defaultTask?.output)
            ? defaultTask.output
            : [],
          shape: mergedShape,
          previousTaskId: previousTaskIdProvided
            ? task.previousTaskId
            : index === 0
            ? null
            : normalizedTasks[index - 1].id,
        };

        normalizedTasks.push(normalizedTask);
      });

      return normalizedTasks;
    },
    getDefaultWorkflowSettings() {
      return {
        databaseType: '',
        showResultsBesideForm: false,
        loadingCaption: '',
        isHtmlElementVisible: true,
        workflowIcon: {
          icon: null,
          linkType: null,
        },
        domains: [],
        executionEndPoint: this.flowRunnerEndpoint || '',
        validationMode: 'jump-to-invalid',
        authentication: {
          authUrl: '',
          location: 'header',
          label: '',
          redirectUrl: '',
          storageOption: '',
        },
        btnCaption: 'Starten',
        fullWidth: false,
        brandingColor: '#000',
      };
    },
    getNormalizedWorkflowSuggestion(workflow = {}) {
      const normalizedWorkflow = { ...workflow };

      if (!normalizedWorkflow.name) {
        normalizedWorkflow.name =
          normalizedWorkflow.caption || 'AI Workflow Draft';
      }

      if (
        !normalizedWorkflow.settings ||
        typeof normalizedWorkflow.settings !== 'object' ||
        Array.isArray(normalizedWorkflow.settings)
      ) {
        normalizedWorkflow.settings = this.getDefaultWorkflowSettings();
      }

      if (!normalizedWorkflow.owner) {
        normalizedWorkflow.owner =
          this.userId || this.$store?.state?.user?.username || '';
      }

      if (Array.isArray(normalizedWorkflow.tasks)) {
        normalizedWorkflow.tasks = this.normalizeWorkflowTasks(
          normalizedWorkflow.tasks
        );
      }

      return normalizedWorkflow;
    },
    getSuggestedTaskIcon(taskName) {
      const icon =
        workFlowTasksMiscData?.[taskName]?.style?.icon || 'code-task.svg';
      return require(`../../assets/img/work-flow-icons/task-icons/${icon}`);
    },
    getWorkflowSuggestionTasks(workflow = {}) {
      const tasks = Array.isArray(workflow?.tasks) ? workflow.tasks : [];
      return tasks.map((task, index) => ({
        ...task,
        caption: task?.caption || task?.name || `Task ${index + 1}`,
      }));
    },
    getParsedWorkflowSuggestion(value) {
      if (!value) return null;
      let parsedValue = value;

      if (typeof value === 'string') {
        try {
          parsedValue = JSON.parse(value);
        } catch (error) {
          const cleanedValue = value
            .replace(/^```(?:json)?\s*/i, '')
            .replace(/\s*```$/, '')
            .trim();
          try {
            parsedValue = JSON.parse(cleanedValue);
          } catch (parseError) {
            return null;
          }
        }
      }

      if (
        parsedValue &&
        parsedValue.version === 3 &&
        Array.isArray(parsedValue.tasks)
      ) {
        return this.getNormalizedWorkflowSuggestion(parsedValue);
      }
      return null;
    },
    getFormattedMessage(text = '') {
      const escapedText = String(text)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');

      if (!this.skillSuggestions.length) return escapedText;

      const normalizedSkillSet = new Set(
        this.skillSuggestions.map((skill) => skill.toLowerCase())
      );

      return escapedText.replace(/@([^\s@]+)/g, (match, mention) => {
        if (normalizedSkillSet.has(String(mention).toLowerCase())) {
          return `<span class="mention-text">${match}</span>`;
        }
        return match;
      });
    },
    async fetchSkillsFromUserTable() {
      if (this.isSkillsLoading || this.skillSuggestions.length) return;

      const cachedSuggestions = getCachedAISkillSuggestions();
      const cachedMetadataMap = getCachedAISkillMetadataMap();
      if (cachedSuggestions.length) {
        this.skillSuggestions = cachedSuggestions;
        this.skillMetadataMap = cachedMetadataMap;
        return;
      }

      this.isSkillsLoading = true;
      try {
        const allSkillNames = await fetchAISkillSuggestions(this.databaseList);
        this.skillSuggestions = Array.isArray(allSkillNames)
          ? allSkillNames
          : [];
        this.skillMetadataMap = getCachedAISkillMetadataMap();
      } catch (error) {
        this.skillSuggestions = [];
        this.skillMetadataMap = {};
      } finally {
        this.isSkillsLoading = false;
      }
    },
    getSkillMetaByName(skillName = '') {
      const key = String(skillName || '')
        .trim()
        .toLowerCase();
      if (!key) return null;
      return this.skillMetadataMap?.[key] || null;
    },
    getDatabaseByName(databaseName = '') {
      if (!databaseName) return null;
      const normalized = String(databaseName).trim().toLowerCase();
      if (!normalized) return null;
      return (
        (this.databaseList || []).find(
          (db) =>
            String(db?.name || '')
              .trim()
              .toLowerCase() === normalized ||
            String(db?.caption || '')
              .trim()
              .toLowerCase() === normalized
        ) || null
      );
    },
    getSkillDatabaseContextBlock(skills = []) {
      if (!Array.isArray(skills) || !skills.length) return '';

      const contextBlocks = [];
      skills.forEach((skillName) => {
        const meta = this.getSkillMetaByName(skillName);
        if (!meta) return;

        const parts = [`Skill: ${meta.name || skillName}`];
        if (meta.context) {
          parts.push(`Skill Context:\n${meta.context}`);
        }

        const targetDatabaseName = meta.databaseName || meta.name;
        if (targetDatabaseName) {
          const linkedDatabase = this.getDatabaseByName(targetDatabaseName);
          const dbType = String(linkedDatabase?.type || '').toLowerCase();
          const isLoadOrTimeTrigger =
            dbType === 'loadprofile' ||
            dbType === 'timetrigger' ||
            dbType === 'timeseries';

          if (linkedDatabase && !isLoadOrTimeTrigger) {
            const rawSchema = linkedDatabase?.schema;
            if (rawSchema) {
              parts.push(
                `Database: ${linkedDatabase?.name || targetDatabaseName}`
              );
              parts.push(`Database Schema:\n${rawSchema}`);
            }
          }
        }

        if (parts.length > 1) {
          contextBlocks.push(parts.join('\n'));
        }
      });

      if (!contextBlocks.length) return '';
      return `\n\nSkill Database Context:\n${contextBlocks.join('\n\n')}`;
    },
    getCurrentDatabaseContextBlock() {
      const db = this.activeDatabaseContext;
      if (!db || typeof db !== 'object') return '';

      const dbType = String(db?.type || '').toLowerCase();
      const isLoadOrTimeTrigger =
        dbType === 'loadprofile' ||
        dbType === 'timetrigger' ||
        dbType === 'timeseries';
      if (isLoadOrTimeTrigger) return '';

      const schema = db?.schema;
      if (!schema) return '';

      return `\n\nCurrent Database Context:\nDatabase: ${
        db?.name || db?.caption || ''
      }\nDatabase Schema:\n${schema}`;
    },
    onPromptInput() {
      this.captureCaretPosition();
      if (this.mentionQuery !== null) {
        this.fetchSkillsFromUserTable();
      }
    },
    captureCaretPosition() {
      const input = this.$refs.promptInputRef;
      if (!input || typeof input.selectionStart !== 'number') return;
      this.caretPosition = input.selectionStart;
    },
    onSelectSkill(skill) {
      const input = this.$refs.promptInputRef;
      if (!input) return;

      const before = this.prompt.slice(0, this.caretPosition);
      const after = this.prompt.slice(this.caretPosition);
      const mentionMatch = before.match(/(^|\s)@([^\s@]*)$/);
      if (!mentionMatch) return;

      const mentionStart =
        before.length - mentionMatch[0].length + mentionMatch[1].length;
      const updatedBefore = `${before.slice(0, mentionStart)}@${skill} `;
      const updatedPrompt = `${updatedBefore}${after}`;
      this.prompt = updatedPrompt;

      this.$nextTick(() => {
        const nextPosition = updatedBefore.length;
        input.focus();
        input.setSelectionRange(nextPosition, nextPosition);
        this.caretPosition = nextPosition;
      });
    },
    onSuggestionClick(text) {
      const current = this.prompt || '';
      const separator = current.trim().length ? ' ' : '';
      this.prompt = `${current}${separator}${text}`;
      this.$nextTick(() => {
        const input = this.$refs.promptInputRef;
        if (!input) return;
        input.focus();
        const cursorPos = this.prompt.length;
        input.setSelectionRange(cursorPos, cursorPos);
        this.caretPosition = cursorPos;
      });
    },
    appendAttachedFiles(files = []) {
      if (!files.length) return;

      files.forEach((file) => {
        if (!(file instanceof File)) return;
        const exists = this.attachedFiles.some(
          (existing) =>
            existing.name === file.name &&
            existing.size === file.size &&
            existing.lastModified === file.lastModified
        );
        if (!exists) {
          this.attachedFiles.push(file);
        }
      });
    },
    onDragEnterFiles() {
      if (this.isFetching) return;
      this.isDraggingFiles = true;
    },
    onDragOverFiles() {
      if (this.isFetching) return;
      this.isDraggingFiles = true;
    },
    onDragLeaveFiles(event) {
      const currentTarget = event?.currentTarget;
      const relatedTarget = event?.relatedTarget;
      if (
        currentTarget &&
        relatedTarget &&
        currentTarget.contains(relatedTarget)
      )
        return;
      this.isDraggingFiles = false;
    },
    onDropFiles(event) {
      this.isDraggingFiles = false;
      if (this.isFetching) return;
      const files = Array.from(event?.dataTransfer?.files || []);
      this.appendAttachedFiles(files);
    },
    removeAttachedFile(index) {
      if (index < 0 || index >= this.attachedFiles.length) return;
      this.attachedFiles.splice(index, 1);
    },
    onKeydown(e) {
      if (this.isFetching) return;
      if (e.key === 'Backspace') {
        const isMentionDeleted = this.handleMentionBackspace();
        if (isMentionDeleted) {
          e.preventDefault();
          return;
        }
      }
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        this.sendMessage();
      }
    },
    handleMentionBackspace() {
      const input = this.$refs.promptInputRef;
      if (!input || typeof input.selectionStart !== 'number') return false;

      const cursorStart = input.selectionStart;
      const cursorEnd = input.selectionEnd;
      if (cursorStart !== cursorEnd) return false;

      const beforeCursor = this.prompt.slice(0, cursorStart);
      const mentionMatch = beforeCursor.match(/(^|\s)(@\S+)\s?$/);
      if (!mentionMatch) return false;

      const mentionToken = mentionMatch[2] || '';
      const mentionName = mentionToken.startsWith('@')
        ? mentionToken.slice(1)
        : '';
      if (!mentionName || !this.skillSuggestions.includes(mentionName)) {
        return false;
      }

      const mentionStartIndex = beforeCursor.lastIndexOf(mentionToken);
      if (mentionStartIndex < 0) return false;

      const mentionEndIndex = mentionStartIndex + mentionToken.length;
      const hasTrailingSpace = this.prompt.charAt(mentionEndIndex) === ' ';
      const removeEndIndex = hasTrailingSpace
        ? mentionEndIndex + 1
        : mentionEndIndex;

      const preservedPrefix = this.prompt.slice(0, mentionStartIndex);
      const suffix = this.prompt.slice(removeEndIndex);
      this.prompt = `${preservedPrefix}${suffix}`;

      this.$nextTick(() => {
        const nextPosition = mentionStartIndex;
        input.focus();
        input.setSelectionRange(nextPosition, nextPosition);
        this.caretPosition = nextPosition;
      });

      return true;
    },
    async sendMessage() {
      const trimmedPrompt = (this.prompt || '').trim();
      if ((!trimmedPrompt && this.attachedFiles.length < 1) || this.isFetching)
        return;
      const mentionedSkills = this.promptMentionedSkills;
      const selectedUsecaseFromPrompt =
        mentionedSkills.length > 1
          ? 'multiple_skill'
          : mentionedSkills[0] || '';
      const userAttachedFiles = [...this.attachedFiles];
      const filesToSend = [...userAttachedFiles];
      const userMessageText = trimmedPrompt || 'Dateien hochgeladen';
      this.messages.push({
        id: Date.now(),
        role: 'user',
        text:
          userAttachedFiles.length > 0
            ? `${userMessageText}\n\nAnhänge: ${userAttachedFiles
                .map((file) => file.name)
                .join(', ')}`
            : userMessageText,
      });

      this.prompt = '';
      this.caretPosition = 0;
      this.attachedFiles = [];
      this.isDraggingFiles = false;
      this.$nextTick(() => this.scrollToBottom());

      try {
        this.isFetching = true;
        const selectedUsecase =
          selectedUsecaseFromPrompt ||
          this.activeTaskUsecase ||
          'codelos_workflow_builder_skill';

        const formData = new FormData();
        const workflowPromptContext =
          this.currentWorkflow && Array.isArray(this.currentWorkflow.tasks)
            ? `\n\nCurrent workflow JSON:\n${JSON.stringify(
                this.currentWorkflow
              )}`
            : '';
        const currentDatabaseContext = this.getCurrentDatabaseContextBlock();
        const promptWithWorkflowOnlyInstruction = `${userMessageText}${currentDatabaseContext}${workflowPromptContext}\n\nMake sure other tasks do not change.\nAlways return full JSON.\n alwasy return workflow only`;
        formData.append(
          'props',
          JSON.stringify({
            prompt: promptWithWorkflowOnlyInstruction,
            model: 'gpt-5',
            attachments: filesToSend.map((file) => file.name),
          })
        );
        formData.append('usecase', selectedUsecase);
        filesToSend.forEach((file) => {
          formData.append('files', file);
        });

        const response = await askChatGPT(this.flowRunnerEndpoint, formData);
        const aiResponseMessage = response?.data?.message ?? response?.data;
        const parsedWorkflow =
          this.getParsedWorkflowSuggestion(aiResponseMessage);

        if (parsedWorkflow) {
          const draftS3Key = await this.createWorkflowDraftUpload(
            parsedWorkflow
          );
          this.messages.push({
            id: `${Date.now()}-workflow`,
            role: 'workflow',
            workflow: parsedWorkflow,
            s3Key: draftS3Key,
          });
          return;
        }

        this.messages.push({
          id: `${Date.now()}-ai`,
          role: 'ai',
          text:
            typeof aiResponseMessage === 'string'
              ? aiResponseMessage
              : 'Die KI-Antwort konnte nicht als Workflow gelesen werden.',
        });
      } catch (error) {
        this.messages.push({
          id: `${Date.now()}-error`,
          role: 'ai',
          text:
            error?.message ||
            'Beim Abrufen der KI-Antwort ist ein Fehler aufgetreten.',
        });
      } finally {
        this.isFetching = false;
        this.$nextTick(() => this.scrollToBottom());
      }
    },
    getUploadResponseS3Key(uploadResponseText = '') {
      const keyMatch = String(uploadResponseText).match(/<Key>([^<]+)<\/Key>/);
      return keyMatch?.[1] || '';
    },
    async createWorkflowDraftUpload(workflowDraft) {
      const draftFileName = 'workflow_builder_skill_draft.json';
      const preSignedData = await getPreSignedUrl(
        'workflow_builder_skill_draft',
        draftFileName,
        {
          auto_delete_after_hours: 2,
        }
      );

      const workflowContent = JSON.stringify(workflowDraft, null, 2);
      const workflowFile = new File([workflowContent], draftFileName, {
        type: 'application/json',
      });

      const uploadResponse = await uploadFile(preSignedData, workflowFile);
      const uploadResponseText = await uploadResponse.text();
      if (!uploadResponse.ok) {
        throw new Error('Workflow-Entwurf konnte nicht hochgeladen werden.');
      }

      const uploadedWorkflowDraftS3Key =
        this.getUploadResponseS3Key(uploadResponseText) ||
        preSignedData?.signature?.key ||
        '';

      if (!uploadedWorkflowDraftS3Key) {
        throw new Error(
          'Entwurf wurde hochgeladen, aber es wurde kein S3-Key zurückgegeben.'
        );
      }
      return uploadedWorkflowDraftS3Key;
    },
    isActionMenuOpen(message, action) {
      return (
        this.actionMenu.messageId === message?.id &&
        this.actionMenu.action === action
      );
    },
    toggleActionMenu(message, action) {
      if (this.isActionMenuOpen(message, action)) {
        this.actionMenu = { messageId: null, action: '' };
        return;
      }
      this.actionMenu = { messageId: message?.id || null, action };
    },
    closeActionMenu() {
      this.actionMenu = { messageId: null, action: '' };
    },
    onEditClick(message, targetMode = 'current') {
      if (this.resolvedIsActionLoading) return;
      const workflow = message?.workflow;
      if (!workflow) return;
      this.closeActionMenu();
      this.localIsActionLoading = true;
      this.localLoadingAction = 'edit';
      this.localLoadingMessageId = message?.id || null;
      this.$emit('applyWorkflowSuggestion', {
        workflow,
        targetMode,
      });
    },
    onSaveClick(message, targetMode = 'current') {
      if (this.resolvedIsActionLoading) return;
      const workflow = message?.workflow;
      if (!workflow) return;
      this.closeActionMenu();
      this.localIsActionLoading = true;
      this.localLoadingAction = 'save';
      this.localLoadingMessageId = message?.id || null;
      this.$emit('saveWorkflow', {
        workflow,
        targetMode,
      });
    },
    onStartenClick(message) {
      const s3Key = message?.s3Key || '';
      if (!s3Key) {
        this.messages.push({
          id: `${Date.now()}-no-s3`,
          role: 'ai',
          text: 'Die Entwurfsdatei ist noch nicht bereit. Bitte erneut senden.',
        });
        this.$nextTick(() => this.scrollToBottom());
        return;
      }
      this.messages.push({
        id: `${Date.now()}-preview`,
        role: 'preview',
        preview: {
          executionUrl: 'https://flow-runner.api.ebv9.codelos.io/',
          flowId: 'workflow-builder-skill-draft',
          params: `?s3_key=${s3Key}`,
        },
      });
      this.$nextTick(() => this.scrollToBottom());
    },
    resetChat() {
      this.prompt = '';
      this.messages = [];
      this.attachedFiles = [];
      this.isDraggingFiles = false;
      this.closeActionMenu();
    },
    scrollToBottom() {
      if (!this.$refs.bodyRef) return;
      this.$refs.bodyRef.scrollTop = this.$refs.bodyRef.scrollHeight;
    },
  },
  mounted() {
    const cachedSuggestions = getCachedAISkillSuggestions();
    const cachedMetadataMap = getCachedAISkillMetadataMap();
    if (cachedSuggestions.length) {
      this.skillSuggestions = cachedSuggestions;
    }
    if (cachedMetadataMap && Object.keys(cachedMetadataMap).length) {
      this.skillMetadataMap = cachedMetadataMap;
    }
    this.fetchSkillsFromUserTable();
    document.addEventListener('click', this.closeActionMenu);
  },
  beforeDestroy() {
    document.removeEventListener('click', this.closeActionMenu);
  },
  watch: {
    isActionLoading(newVal) {
      if (!newVal) {
        this.localIsActionLoading = false;
        this.localLoadingAction = '';
        this.localLoadingMessageId = null;
      }
    },
  },
  components: {
    ProcessPreview,
  },
};
</script>

<style scoped>
.ai-skill-overlay {
  position: fixed;
  inset: 0;
  z-index: 12020;
  background: transparent;
  display: flex;
  justify-content: flex-end;
  align-items: flex-end;
  padding: 0 20px 90px 0;
}

.ai-skill-panel {
  height: min(704px, calc(100vh - 160px));
  width: 400px;
  min-width: 400px;
  flex: 0 0 400px;
  max-width: calc(100vw - 20px);
  background: #fff;
  border: 1px solid #efefef;
  border-radius: 14px;
  box-shadow: rgba(9, 14, 21, 0.16) 0px 5px 40px 0px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  transform-origin: right bottom;
  animation: chatbot-scale-in 0.2s ease-out;
}

.panel-header {
  min-height: 55px;
  border-bottom: 1px solid #f5f5f5;
  padding: 0 14px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #fff;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 700;
  color: #111;
  font-size: 16px;
}

.header-left img {
  width: 28px;
  height: 28px;
  border-radius: 50%;
}

.beta-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 2px 8px;
  border-radius: 999px;
  border: 1px solid #111;
  background: #fff;
  color: #111;
  font-size: 11px;
  font-weight: 700;
  line-height: 1.2;
}

.header-right {
  display: flex;
  gap: 6px;
}

.header-right button {
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 8px;
  background: transparent;
  color: #262626;
}

.header-right button:hover {
  background: #f3f3f3;
}

.panel-body {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 18px 16px;
  font-size: 14px;
  color: #222;
}

.welcome-state {
  text-align: center;
}

.welcome-avatar {
  width: 56px;
  height: 56px;
  margin: 0 auto 12px;
  border-radius: 14px;
  background: #f5f5f5;
  display: flex;
  align-items: center;
  justify-content: center;
}

.welcome-avatar img {
  width: 28px;
  height: 28px;
}

.welcome-state h4 {
  color: #111;
  font-size: 18px;
  margin-bottom: 14px;
}

.suggestion-list {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  justify-content: center;
  flex-direction: column;
  align-items: center;
}

.suggestion-item {
  text-align: center;
  border: 1px solid #e4e4e4;
  border-radius: 999px;
  background: #fff;
  padding: 6px 10px;
  color: #2a2a2a;
  font-size: 12px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.suggestion-item:hover {
  border-color: #c9c9c9;
  background: #f6f6f6;
}

.messages-list {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.message-row {
  display: flex;
}

.message-row.user {
  justify-content: flex-end;
}

.message-row.ai {
  justify-content: flex-start;
}

.message-row.workflow {
  justify-content: flex-start;
  width: 100%;
  min-width: 0;
}

.message-bubble {
  max-width: 320px;
  padding: 12px 16px;
  border-radius: 20px;
  font-size: 14px;
  line-height: 1.5;
}

.message-row.user .message-bubble {
  background: #333;
  color: #fff;
}

.message-row.ai .message-bubble {
  background: #f5f5f5;
  color: #111;
}

.message-row.ai .message-bubble.message-loading {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-width: 52px;
}

.message-row.ai .message-bubble.message-loading .dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #646464;
  animation: chatbot-typing 1.2s infinite ease-in-out;
}

.message-row.ai .message-bubble.message-loading .dot:nth-child(2) {
  animation-delay: 0.15s;
}

.message-row.ai .message-bubble.message-loading .dot:nth-child(3) {
  animation-delay: 0.3s;
}

.panel-input {
  border-top: none;
  padding: 0 16px 16px;
  display: flex;
  gap: 8px;
  align-items: flex-end;
  background: #fff;
}

.panel-input .input-area {
  flex: 1;
  position: relative;
  border-radius: 24px;
  box-shadow: rgba(9, 14, 21, 0.16) 0px 0px 4px 0px;
  border: 1px solid #f5f5f5;
  overflow: visible;
}

.panel-input .input-area.is-dragging-files {
  border: 1px dashed #111;
  background: #fafafa;
}

.panel-input .context-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin: 8px 10px 0;
  border-radius: 999px;
  border: 1px solid #d6dce4;
  background: #f4f7fa;
  color: #314355;
  font-size: 12px;
  line-height: 16px;
  padding: 4px 10px;
  max-width: calc(100% - 20px);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.panel-input textarea {
  width: 100%;
  min-height: 64px;
  max-height: 140px;
  border: none;
  border-radius: 0;
  padding: 12px 12px 2px 14px;
  color: #111;
  resize: none;
  outline: none;
  background: #fff;
  line-height: 20px;
  overflow-y: auto;
}

.panel-input .active-mentions {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  padding: 0 10px 10px 10px;
}

.panel-input .active-mentions .mention-chip {
  border-radius: 999px;
  border: 1px solid #d4dce5;
  background: #f1f5f9;
  color: #324d67;
  font-size: 12px;
  line-height: 16px;
  padding: 3px 8px;
}

.panel-input textarea:focus {
  border: none;
}

.panel-input .skill-mention-list {
  position: absolute;
  left: 0;
  right: 0;
  bottom: calc(100% + 6px);
  z-index: 20;
  max-height: 210px;
  overflow-y: auto;
  margin: 0;
  padding: 6px 0;
  list-style: none;
  border: 1px solid #d9d9d9;
  border-radius: 10px;
  background: #fff;
  box-shadow: 0 8px 22px rgba(0, 0, 0, 0.14);
}

.panel-input .skill-mention-list li {
  padding: 8px 12px;
  font-size: 13px;
  color: #111;
  cursor: pointer;
}

.panel-input .skill-mention-list li:hover {
  background: #f3f3f3;
}

.panel-input button {
  width: 32px;
  min-width: 32px;
  height: 32px;
  border: none;
  border-radius: 50%;
  background: #111;
  color: #fff;
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 0 8px 8px 0;
}

.panel-input button:disabled {
  opacity: 1;
  border-color: #cfcfcf;
  background: #e7e7e7;
  color: #8f8f8f;
  cursor: not-allowed;
}

.panel-input .attached-files {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  padding: 8px 10px 0;
}

.panel-input .attached-file-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  max-width: 100%;
  padding: 4px 8px;
  border-radius: 999px;
  border: 1px solid #d9d9d9;
  background: #f7f7f7;
  font-size: 12px;
}

.panel-input .attached-file-chip .name {
  max-width: 210px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.panel-input .attached-file-chip .remove-chip {
  width: 16px;
  min-width: 16px;
  height: 16px;
  margin: 0;
  border: none;
  border-radius: 50%;
  background: #e5e5e5;
  color: #333;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  padding: 0;
}

.workflow-suggestion {
  margin-top: 10px;
  border: 1px solid #eceff3;
  border-radius: 12px;
  background: #fafbfc;
  padding: 12px;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  overflow-x: hidden;
}

.workflow-suggestion h5 {
  margin: 0 0 8px;
  font-size: 14px;
  color: #111;
}

.workflow-suggestion .step-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.workflow-suggestion .step-item {
  display: flex;
  align-items: center;
  gap: 10px;
  background: #fff;
  border: 1px solid #e6eaf0;
  border-radius: 10px;
  padding: 8px 10px;
}

.workflow-suggestion .step-item .task-icon-wrapper {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: #f5f7fa;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.workflow-suggestion .step-item .task-icon {
  width: 18px;
  height: 18px;
}

.workflow-suggestion .step-caption {
  color: #2d3748;
  font-size: 13px;
  line-height: 1.35;
}

.workflow-action-buttons {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 8px;
  margin-top: 10px;
  align-items: stretch;
}

.workflow-action-buttons .action-split {
  position: relative;
  display: flex;
  width: 100%;
  min-width: 0;
}

.workflow-action-buttons button {
  height: 36px;
  border-radius: 8px;
  border: 1px solid #d7dce2;
  background: #fff;
  color: #1f2937;
  font-weight: 600;
  font-size: 13px;
}

.workflow-action-buttons .action-split > button.secondary,
.workflow-action-buttons .action-split > button.primary {
  width: 100%;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 8px;
}

.workflow-action-buttons .with-chevron .btn-label {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.workflow-action-buttons .with-chevron .inline-chevron {
  padding: 0 8px;
  font-size: 11px;
  cursor: pointer;
}

.workflow-action-buttons .primary.with-chevron .inline-chevron {
  color: #fff;
}

.workflow-action-buttons .split-menu {
  position: absolute;
  bottom: calc(100% + 6px);
  right: 0;
  z-index: 5;
  min-width: 170px;
  border: 1px solid #d7dce2;
  border-radius: 8px;
  background: #fff;
  box-shadow: 0 8px 18px rgba(0, 0, 0, 0.12);
  overflow: hidden;
}

.workflow-action-buttons .split-menu button {
  width: 100%;
  border: none;
  border-radius: 0;
  height: 34px;
  background: #fff;
  color: #111;
  text-align: left;
  padding: 0 10px;
  font-size: 12px;
  font-weight: 500;
}

.workflow-action-buttons .split-menu button:hover {
  background: #f4f4f4;
}

.workflow-action-buttons button.primary {
  background: #111;
  border-color: #111;
  color: #fff;
}

.workflow-action-buttons button:hover {
  background: #f5f6f7;
}

.workflow-action-buttons button.primary:hover {
  background: #222;
}

.start-preview {
  margin-top: 10px;
  border: 1px solid #eceff3;
  border-radius: 12px;
  overflow: hidden;
  overscroll-behavior: contain;
  touch-action: pan-y;
  background: #fff;
  height: 420px;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
}

.start-preview ::v-deep .form-builder-view {
  height: 100%;
  width: 100%;
  max-width: 100%;
  overflow-x: hidden;
}

.start-preview ::v-deep .tab-content-wrapper {
  overflow-y: auto !important;
  overscroll-behavior: contain;
  -webkit-overflow-scrolling: touch;
}

.start-preview ::v-deep iframe {
  width: 100% !important;
  max-width: 100% !important;
  display: block;
}

@media (max-width: 768px) {
  .ai-skill-overlay {
    padding: 0 12px 84px;
  }

  .ai-skill-panel {
    width: 100%;
    min-width: 0;
    flex: 1 1 auto;
    max-width: 100%;
    height: min(660px, calc(100vh - 140px));
  }
}

@keyframes chatbot-scale-in {
  from {
    opacity: 0;
    transform: scale(0.92);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

@keyframes chatbot-typing {
  0%,
  80%,
  100% {
    opacity: 0.35;
    transform: translateY(0);
  }
  40% {
    opacity: 1;
    transform: translateY(-2px);
  }
}

.message-bubble ::v-deep .mention-text {
  display: inline-block;
  border-radius: 10px;
  border: 1px solid #c5d0dc;
  background: #edf3f8;
  color: #304a65;
  padding: 1px 6px;
  margin: 0 1px;
  font-weight: 600;
}
</style>
